import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import { useMemo } from 'react';

export type PaymentRecord = {
  id: string;
  amount: number;
  currency: string | null;
  status: 'pending' | 'paid' | 'expired';
  payment_method: string | null;
  transaction_id: string | null;
  notes: string | null;
  due_at: string;
  paid_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  clients: {
    id: string;
    name: string;
    status: string;
    phone: string;
    whatsapp_jid: string | null;
  };
};

export type Payment = {
  id: string;
  clientName: string;
  clientId: string;
  clientStatus: string;
  clientPhone: string;
  whatsappJid: string | null;
  amount: number;
  currency: string;
  paymentMethod: string | null;
  transactionId: string | null;
  notes: string | null;
  status: 'pending' | 'paid' | 'expired';
  dueDate: string;
  dueTime: string;
  dueAt: string;
  paidAt: string | null;
  category: 'today' | 'upcoming' | 'overdue' | 'paid';
};

async function fetchAllPayments(accessToken: string | null | undefined) {
  return apiRequest<PaymentRecord[]>('/api/payments', { method: 'GET' }, accessToken);
}

export function useAllPayments() {
  const { session } = useAuth();

  const { data: paymentsData, isLoading } = useQuery<PaymentRecord[]>({
    queryKey: ['payments'],
    enabled: Boolean(session),
    queryFn: () => fetchAllPayments(session?.access_token),
  });

  const payments = useMemo(() => {
    if (!paymentsData) {
      return { today: [], upcoming: [], overdue: [], paid: [] };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const categorizedPayments = paymentsData.reduce(
      (acc, payment) => {
        const dueDate = new Date(payment.due_at);
        let category: 'today' | 'upcoming' | 'overdue' | 'paid';

        // Paid payments go to paid tab (last 30 days only)
        if (payment.status === 'paid') {
          const paidDate = payment.paid_at ? new Date(payment.paid_at) : dueDate;
          if (paidDate >= thirtyDaysAgo) {
            category = 'paid';
          } else {
            return acc; // Skip payments paid more than 30 days ago
          }
        } else if (dueDate < today) {
          category = 'overdue';
        } else if (dueDate >= today && dueDate < tomorrow) {
          category = 'today';
        } else {
          category = 'upcoming';
        }

        const formattedPayment: Payment = {
          id: payment.id,
          clientName: payment.clients.name,
          clientId: payment.clients.id,
          clientStatus: payment.clients.status,
          clientPhone: payment.clients.phone,
          whatsappJid: payment.clients.whatsapp_jid,
          amount: Number(payment.amount),
          currency: payment.currency ?? 'INR',
          paymentMethod: payment.payment_method,
          transactionId: payment.transaction_id,
          notes: payment.notes,
          status: payment.status,
          dueDate: dueDate.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          dueTime: dueDate.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
          }),
          dueAt: payment.due_at,
          paidAt: payment.paid_at,
          category,
        };

        acc[category].push(formattedPayment);
        return acc;
      },
      {
        today: [],
        upcoming: [],
        overdue: [],
        paid: [],
      } as {
        [key in 'today' | 'upcoming' | 'overdue' | 'paid']: Payment[];
      }
    );

    // Sort each category
    categorizedPayments.today.sort(
      (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
    );
    categorizedPayments.upcoming.sort(
      (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
    );
    categorizedPayments.overdue.sort(
      (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
    );
    categorizedPayments.paid.sort(
      (a, b) => new Date(b.paidAt || b.dueAt).getTime() - new Date(a.paidAt || a.dueAt).getTime()
    );

    return categorizedPayments;
  }, [paymentsData]);

  return { payments, isLoading };
}
